import type { ColumnDef } from "@tanstack/react-table";
import { capitalize, formatIntToCurrency, highlightText } from "@repo/utils";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";

import type { ProductModifier } from "~/providers/product-provider";
import { SettingsHeader } from "~/components/layout/settings-header";
import { DataTable } from "~/components/miscellaneous/data-table";
import { DataTableColumnHeader } from "~/components/miscellaneous/data-table-column-header";
import { useProduct } from "~/hooks/use-product";
import { useProductModifierManager } from "~/managers/use-product-modifier-manager";

export const Route = createFileRoute("/_main/settings/_layout/modifiers/")({
  component: Modifiers,
});

function Modifiers() {
  const { t } = useTranslation("common");

  const { modifiers } = useProduct();

  const { openProductModifierDialog } = useProductModifierManager();

  const columns: ColumnDef<ProductModifier>[] = [
    {
      id: t("name"),
      accessorKey: "name",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("name")} />
      ),
      cell: ({ row: { original: product }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html: highlightText(product.name, globalFilter) ?? "",
            }}
          ></p>
        );
      },
    },
    {
      id: t("price"),
      accessorKey: "prices",
      filterFn: "includesString",
      size: 200,
      header: ({ column }) => (
        <DataTableColumnHeader column={column} title={t("price")} />
      ),
      cell: ({ row: { original: product }, table }) => {
        const globalFilter = table.getState().globalFilter as string;

        return (
          <p
            dangerouslySetInnerHTML={{
              __html:
                highlightText(
                  product.prices
                    .map((price) => formatIntToCurrency(price.value, true))
                    .join(" - "),
                  globalFilter,
                ) ?? "",
            }}
          ></p>
        );
      },
    },
  ];

  return (
    <>
      <SettingsHeader
        title={t("ProductModifier.product_modifiers")}
        buttonLabel={capitalize(t("ProductModifier.new_product_modifier"))}
        buttonAction={() => openProductModifierDialog()}
      />

      <DataTable
        defaultSorting={[{ id: t("name"), desc: false }]}
        columns={columns}
        data={modifiers}
        onSelect={openProductModifierDialog}
      />
    </>
  );
}
